import { isEqual } from "lodash";
import React, { createContext, Dispatch, useContext, useEffect, useReducer } from "react";
import {
  blockReducer,
  CHANGE_PUSHED,
  defaultBlockState,
  IBlockAction,
  IBlockState,
  SET_STATE,
} from "./blockReducer";
import { AibBlockLine } from "./interface";

interface BlockContextProps<T> {
  state: IBlockState<T>;
  dispatch: Dispatch<IBlockAction<T>>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const BlockContext = createContext<BlockContextProps<any>>({
  state: defaultBlockState,
  dispatch:
    // istanbul ignore next
    () => {},
});

interface BlockContextProviderProps<T> {
  initialState: IBlockState<T>;
  children: React.ReactNode;
  onChange: Dispatch<AibBlockLine<T>[]>;
}

/**
 * Provides block context
 *
 * @param props - Destructured parameter object
 * @param props.initialState - Initial block state used to seed the provider
 * @param props.children - React children to render within the provider
 * @param props.onChange - Callback invoked with the updated lines when the block state is pushed
 */
const BlockContextProviderInner = <T,>({
  initialState,
  children,
  onChange,
}: BlockContextProviderProps<T>): React.ReactElement => {
  const [state, dispatch] = useReducer(blockReducer<T>, initialState);

  // Update or push state
  useEffect(() => {
    if (!isEqual(state, initialState)) {
      if (state.changed) {
        onChange(state.lines);
        dispatch({ operation: CHANGE_PUSHED });
      } else {
        dispatch({ operation: SET_STATE, ...initialState });
      }
    }
  }, [initialState, onChange, state]);

  return <BlockContext.Provider value={{ state, dispatch }}>{children}</BlockContext.Provider>;
};

export const BlockContextProvider = React.memo(BlockContextProviderInner) as <T>(
  props: BlockContextProviderProps<T>,
) => React.ReactElement;

/**
 * Custom hook to use the context
 */
export function useBlockContext<T>() {
  const { state, dispatch } = useContext(BlockContext as React.Context<BlockContextProps<T>>);
  return { state, dispatch };
}

BlockContextProviderInner.displayName = "BlockContextProvider";
